import { ReactNode } from "react";
import { useParams, Navigate } from "react-router-dom";
import { OnboardingGateBase } from "@core/ui";
import { useOnboardingGate } from "@core/features/onboarding";
import { Loader } from "@themes/pollinator/ui/Loader";
import { usePlatformStore } from "@platform/store";

export type Props = {
  /** Children to render if onboarding is complete */
  children: ReactNode;
};

/**
 * Gate component that redirects to onboarding if not complete.
 * Wraps dashboard routes to ensure users complete onboarding first.
 *
 * Extracts programId from URL params and user from the platform store,
 * then uses useOnboardingGate to determine if the user should be blocked.
 *
 * @example
 * ```tsx
 * // In router
 * <OnboardingGate>
 *   <DashboardPage />
 * </OnboardingGate>
 * ```
 */
export function OnboardingGate({ children }: Props) {
  const { id, programId: programIdParam } = useParams<{ id: string; programId: string }>();
  const programId = (id ?? programIdParam) ? Number(id ?? programIdParam) : undefined;
  const user = usePlatformStore((s) => s.user);

  const { shouldRedirect, redirectPath, isLoading } = useOnboardingGate({
    programId,
    user,
  });

  // If no program ID, allow through (will be handled by other logic)
  if (!programId) {
    return <>{children}</>;
  }

  return (
    <OnboardingGateBase
      shouldBlock={shouldRedirect}
      isLoading={isLoading}
      renderLoading={() => <Loader isFullPage />}
      renderBlocked={() =>
        redirectPath ? <Navigate to={redirectPath} replace /> : null
      }
    >
      {children}
    </OnboardingGateBase>
  );
}

export default OnboardingGate;


